import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["canvas", "input"]
  static values = { disabled: Boolean }

  connect() {
    if (this.disabledValue) return

    this.canvas = this.canvasTarget
    this.ctx = this.canvas.getContext("2d")
    this.isDrawing = false
    this.lastX = 0
    this.lastY = 0

    // Set up canvas context
    this.ctx.strokeStyle = "#000000"
    this.ctx.lineWidth = 2
    this.ctx.lineCap = "round"
    this.ctx.lineJoin = "round"

    // Resize canvas to match display size
    this.resizeCanvas()

    // Load existing signature if present
    if (this.hasInputTarget && this.inputTarget.value) {
      this.loadSignature(this.inputTarget.value)
    }

    // Bind event listeners
    this.canvas.addEventListener("mousedown", this.startDrawing.bind(this))
    this.canvas.addEventListener("mousemove", this.draw.bind(this))
    this.canvas.addEventListener("mouseup", this.stopDrawing.bind(this))
    this.canvas.addEventListener("mouseout", this.stopDrawing.bind(this))

    // Touch support
    this.canvas.addEventListener("touchstart", this.handleTouchStart.bind(this))
    this.canvas.addEventListener("touchmove", this.handleTouchMove.bind(this))
    this.canvas.addEventListener("touchend", this.stopDrawing.bind(this))

    // Handle resize
    window.addEventListener("resize", this.resizeCanvas.bind(this))
  }

  disconnect() {
    window.removeEventListener("resize", this.resizeCanvas.bind(this))
  }

  resizeCanvas() {
    const rect = this.canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    
    // Save current content
    const imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height)
    
    // Resize
    this.canvas.width = rect.width * dpr
    this.canvas.height = rect.height * dpr
    
    // Scale for retina
    this.ctx.scale(dpr, dpr)
    
    // Restore settings
    this.ctx.strokeStyle = "#000000"
    this.ctx.lineWidth = 2
    this.ctx.lineCap = "round"
    this.ctx.lineJoin = "round"
    
    // Restore content
    this.ctx.putImageData(imageData, 0, 0)
  }

  getCoordinates(event) {
    const rect = this.canvas.getBoundingClientRect()
    const scaleX = this.canvas.width / rect.width
    const scaleY = this.canvas.height / rect.height
    
    return {
      x: (event.clientX - rect.left) * scaleX / (window.devicePixelRatio || 1),
      y: (event.clientY - rect.top) * scaleY / (window.devicePixelRatio || 1)
    }
  }

  startDrawing(event) {
    if (this.disabledValue) return
    
    this.isDrawing = true
    const coords = this.getCoordinates(event)
    this.lastX = coords.x
    this.lastY = coords.y
  }

  draw(event) {
    if (!this.isDrawing || this.disabledValue) return

    const coords = this.getCoordinates(event)
    
    this.ctx.beginPath()
    this.ctx.moveTo(this.lastX, this.lastY)
    this.ctx.lineTo(coords.x, coords.y)
    this.ctx.stroke()

    this.lastX = coords.x
    this.lastY = coords.y
  }

  stopDrawing() {
    if (this.isDrawing) {
      this.isDrawing = false
      this.saveSignature()
    }
  }

  handleTouchStart(event) {
    event.preventDefault()
    if (this.disabledValue) return
    
    const touch = event.touches[0]
    const mouseEvent = new MouseEvent("mousedown", {
      clientX: touch.clientX,
      clientY: touch.clientY
    })
    this.startDrawing(mouseEvent)
  }

  handleTouchMove(event) {
    event.preventDefault()
    if (this.disabledValue) return
    
    const touch = event.touches[0]
    const mouseEvent = new MouseEvent("mousemove", {
      clientX: touch.clientX,
      clientY: touch.clientY
    })
    this.draw(mouseEvent)
  }

  clear() {
    if (this.disabledValue) return
    
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  saveSignature() {
    if (this.hasInputTarget) {
      // Check if canvas is empty
      const imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height)
      const isEmpty = !imageData.data.some((channel, index) => {
        // Check alpha channel (every 4th value starting from 3)
        return index % 4 === 3 && channel !== 0
      })

      if (isEmpty) {
        this.inputTarget.value = ""
      } else {
        this.inputTarget.value = this.canvas.toDataURL("image/png")
      }
      
      this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  loadSignature(dataUrl) {
    if (!dataUrl) return

    const img = new Image()
    img.onload = () => {
      this.ctx.drawImage(img, 0, 0)
    }
    img.src = dataUrl
  }
}
